import { FC } from 'react';

import { useMutation } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import {
    Box,
    Loading,
    LoadingButton,
    Stack,
    Typography,
} from '@thirdspacelearning/library';

import { initiateSelfLaunchedSession } from '@api';
import { useAppDispatch } from '@store';
import { loadAppData } from '@store/initialisation';
import { studyBuddySvg } from '@assets/dashboard/los-panel';

import { SubjectDirectory } from '@types';

const sx = {
    wrapper: {
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        width: '100%',
        px: 4,
        py: 2,
        gap: 2,
    },
    actions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        flexShrink: 0,
    },
    studyBuddy: {
        display: { xs: 'none', md: 'block' },
    },
};

type Props = {
    lesson: SubjectDirectory;
};

const SessionStartLesson: FC<Props> = ({ lesson }) => {
    const dispatch = useAppDispatch();

    const { subject_id, subject_title } = lesson;

    const { mutate: resumeSession, isPending } = useMutation({
        mutationFn: () => initiateSelfLaunchedSession(Number(subject_id), true),
        onSuccess: () => {
            dispatch(loadAppData());
        },
        onError: (error: Error) => {
            enqueueSnackbar('Please try again or refresh the page', {
                title: error.message,
                testId: 'start-lesson-error',
                variant: 'sxSnackbar',
                severity: 'error',
            });
        },
    });

    return (
        <Stack sx={sx.wrapper}>
            <Stack>
                <Typography
                    variant="h5"
                    fontWeight="bold"
                    data-testid="session-resume-title"
                >
                    Let’s start a new lesson when you are ready
                </Typography>
                <Typography
                    variant="subtitle2"
                    data-testid="session-resume-lo-title"
                >
                    This lesson will be "{subject_title}"
                </Typography>
            </Stack>
            <Stack sx={sx.actions}>
                <LoadingButton
                    variant="contained"
                    loading={isPending}
                    loadingIndicator={<Loading size={24} />}
                    onClick={() => resumeSession()}
                    data-testid="start-lesson-btn"
                >
                    Start Lesson
                </LoadingButton>
                <Box
                    component="img"
                    alt=""
                    src={studyBuddySvg}
                    sx={sx.studyBuddy}
                />
            </Stack>
        </Stack>
    );
};

export default SessionStartLesson;
